
<template>
  <footer class="pp-footer">

    <!-- ======================================
         NEWSLETTER STRIP
    ======================================= -->
    <div class="pp-newsletter">
      <div class="container">
        <div class="pp-newsletter-inner">

          <div class="pp-newsletter-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <rect x="2" y="5" width="20" height="14" rx="2"/>
              <path d="m3 7 9 7 9-7"/>
            </svg>
          </div>

          <div class="pp-newsletter-copy">
            <h3>Gardez une longueur d'avance</h3>
            <p>
              Découvrez les nouveautés et les conseils
              pour bien choisir vos pneus.
            </p>
          </div>

          <form
            class="pp-newsletter-form"
            @submit.prevent="submitNewsletter"
          >
            <label for="newsletter-email" class="sr-only">
              Votre adresse e-mail
            </label>

            <input
              id="newsletter-email"
              v-model.trim="newsletterEmail"
              type="email"
              placeholder="Votre adresse e-mail"
              autocomplete="email"
              required
            />

            <button type="submit">
              S'inscrire
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4 12h16m-7-7 7 7-7 7"/>
              </svg>
            </button>
          </form>

        </div>

        <p v-if="newsletterNotice" class="pp-newsletter-notice" role="status">
          {{ newsletterNotice }}
        </p>
      </div>
    </div>

    <!-- ======================================
         MAIN FOOTER
    ======================================= -->
    <div class="pp-footer-main">
      <div class="container">

        <div class="pp-footer-grid">

          <!-- COLUMN 1: BRAND -->
          <div class="pp-footer-brand">

            <router-link
              to="/"
              class="pp-footer-logo"
              aria-label="ProduPneu - Accueil"
            >
              <img
                src="/assets/images/Logo/logo-white.png"
                alt="ProduPneu.fr"
                loading="lazy"
              />
            </router-link>

            <p class="pp-brand-description">
              Votre boutique en ligne dédiée aux pneus.
              Retrouvez différentes marques, comparez les
              caractéristiques et choisissez les modèles
              adaptés à votre véhicule.
            </p>

            <div class="pp-brand-tagline">
              <span></span>
              VOS PNEUS EN TOUTE CONFIANCE
            </div>

            <!-- SOCIAL LINKS -->
            <div class="pp-socials">

              <a
                v-if="socials.facebook"
                :href="socials.facebook"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="ProduPneu sur Facebook"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M13.5 21v-8h2.7l.4-3.1h-3.1V8c0-.9.3-1.5 1.6-1.5h1.7V3.7c-.3 0-1.3-.1-2.4-.1-2.5 0-4.2 1.5-4.2 4.3v2H7.5V13h2.7v8h3.3z"
                  />
                </svg>
              </a>

              <a
                v-if="socials.instagram"
                :href="socials.instagram"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="ProduPneu sur Instagram"
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="5"/>
                  <circle cx="12" cy="12" r="4"/>
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
                </svg>
              </a>

              <a
                v-if="socials.tiktok"
                :href="socials.tiktok"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="ProduPneu sur TikTok"
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M13 3v12.3a4.3 4.3 0 1 1-3.2-4.2"/>
                  <path d="M13 3c.5 3.4 2.6 5.1 6 5.2"/>
                </svg>
              </a>

            </div>
          </div>

          <!-- COLUMN 2: TYRES -->
          <div class="pp-footer-column">
            <h3>Nos pneus</h3>
            <span class="pp-title-line"></span>

            <ul>
              <li>
                <router-link to="/products">
                  Tous les pneus
                </router-link>
              </li>

              <li>
                <router-link
                  :to="{ path: '/products', query: { season: 'summer' } }"
                >
                  Pneus été
                </router-link>
              </li>

              <li>
                <router-link
                  :to="{ path: '/products', query: { season: 'winter' } }"
                >
                  Pneus hiver
                </router-link>
              </li>

              <li>
                <router-link
                  :to="{ path: '/products', query: { season: 'all-season' } }"
                >
                  Pneus 4 saisons
                </router-link>
              </li>

              <li>
                <router-link to="/brands">
                  Toutes les marques
                </router-link>
              </li>

              <li>
                <router-link
                  :to="{ path: '/products', query: { brand: 'Michelin' } }"
                >
                  Pneus Michelin
                </router-link>
              </li>

              <li>
                <router-link
                  :to="{ path: '/products', query: { brand: 'Continental' } }"
                >
                  Pneus Continental
                </router-link>
              </li>
            </ul>
          </div>

          <!-- COLUMN 3: HELP -->
          <div class="pp-footer-column">
            <h3>Aide & conseils</h3>
            <span class="pp-title-line"></span>

            <ul>
              <li>
                <router-link to="/contact">
                  Nous contacter
                </router-link>
              </li>

              <li>
                <router-link to="/delivery">
                  Livraison de pneus
                </router-link>
              </li>

              <li>
                <router-link to="/cart">
                  Mon panier
                </router-link>
              </li>

              <li>
                <router-link
                  :to="isAuthenticated ? '/my-orders' : '/login?redirect=%2Fmy-orders'"
                >
                  Suivre mes commandes
                </router-link>
              </li>

              <li>
                <router-link
                  :to="isAuthenticated
                    ? (isAdmin ? '/admin' : '/account')
                    : '/login'"
                >
                  Mon compte
                </router-link>
              </li>

              <li>
                <router-link to="/contact">
                  Conseils sur les dimensions
                </router-link>
              </li>
            </ul>
          </div>

          <!-- COLUMN 4: COMPANY / CONTACT -->
          <div class="pp-footer-column pp-contact-column">
            <h3>À propos & contact</h3>
            <span class="pp-title-line"></span>

            <ul class="pp-company-links">
              <li>
                <router-link to="/">
                  Découvrir ProduPneu
                </router-link>
              </li>

              <li>
                <router-link to="/contact">
                  Service client
                </router-link>
              </li>
            </ul>

            <div class="pp-contact-items">

              <!-- Email shown only when configured -->
              <a
                v-if="contact.email"
                :href="'mailto:' + contact.email"
                class="pp-contact-item"
              >
                <span class="pp-contact-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="5" width="18" height="14" rx="2"/>
                    <path d="m3 7 9 7 9-7"/>
                  </svg>
                </span>

                <span>{{ contact.email }}</span>
              </a>

              <!-- Phone shown only when configured -->
              <a
                v-if="contact.phone"
                :href="'tel:' + contact.phone.replace(/[^+\d]/g, '')"
                class="pp-contact-item"
              >
                <span class="pp-contact-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 16.4v3a2 2 0 0 1-2.2 2A18.5 18.5 0 0 1 2.6 5.2 2 2 0 0 1 4.6 3h3a2 2 0 0 1 2 1.7l.5 3a2 2 0 0 1-.6 1.8l-1.7 1.7a15 15 0 0 0 5 5l1.7-1.7a2 2 0 0 1 1.8-.6l3 .5a2 2 0 0 1 1.7 2z"
                    />
                  </svg>
                </span>

                <span>{{ contact.phone }}</span>
              </a>

              <router-link
                to="/contact"
                class="pp-contact-item"
              >
                <span class="pp-contact-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M4 4h16v13H8l-4 4V4z"/>
                    <path d="M8 9h8M8 13h5"/>
                  </svg>
                </span>

                <span>Envoyer un message</span>
              </router-link>

            </div>

            <router-link
              to="/contact"
              class="pp-contact-button"
            >
              Contacter notre équipe
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4 12h16m-7-7 7 7-7 7"/>
              </svg>
            </router-link>

          </div>
        </div>

        <!-- PAYMENT / REASSURANCE ROW -->
        <div class="pp-footer-assurance">

          <div class="pp-assurance-item">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 2 4 6v6c0 5 3 8 8 10 5-2 8-5 8-10V6z"/>
              <path d="m8 12 3 3 5-6"/>
            </svg>
            <span>Confidentialité de vos données</span>
          </div>

          <div class="pp-assurance-item">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9"/>
              <path d="M12 7v5l3 2"/>
            </svg>
            <span>Recherche rapide et simple</span>
          </div>

          <div class="pp-assurance-item">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M2 7h12v10H2zM14 10h4l4 4v3h-8z"/>
              <circle cx="6" cy="18" r="2"/>
              <circle cx="18" cy="18" r="2"/>
            </svg>
            <span>Différentes options de livraison</span>
          </div>

        </div>

      </div>
    </div>

    <!-- ======================================
         COPYRIGHT / LEGAL
    ======================================= -->
    <div class="pp-footer-bottom">
      <div class="container">
        <div class="pp-footer-bottom-inner">

          <p>
            © {{ currentYear }}
            <strong>ProduPneu.fr</strong>
            — Tous droits réservés.
          </p>

          <nav class="pp-legal-links" aria-label="Informations légales">
            <router-link to="/legal">
              Mentions légales
            </router-link>

            <router-link to="/privacy">
              Confidentialité
            </router-link>

            <router-link to="/terms">
              CGV
            </router-link>

            <button
              type="button"
              @click="scrollToTop"
              class="pp-back-top"
              aria-label="Retour en haut de page"
            >
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 20V4m-7 7 7-7 7 7"/>
              </svg>
            </button>
          </nav>

        </div>
      </div>
    </div>

  </footer>
</template>

<script>
export default {
  name: "Footer",

  data() {
    return {
      newsletterEmail: "",
      newsletterNotice: "",

      // Add actual business contacts here.
      // Empty values are hidden automatically.
      contact: {
        email: "",
        phone: "",
      },

      // Add actual ProduPneu social accounts.
      socials: {
        facebook: "",
        instagram: "",
        tiktok: "",
      },
    };
  },

  computed: {
    currentYear() {
      return new Date().getFullYear();
    },

    isAuthenticated() {
      return Boolean(
        this.$store.getters["auth/isAuthenticated"]
      );
    },

    isAdmin() {
      return Boolean(
        this.$store.getters["auth/isAdmin"]
      );
    },
  },

  methods: {
    submitNewsletter() {
      // No newsletter backend is connected yet.
      // Do not show fake subscription success.
      this.newsletterNotice =
        "L'inscription à la newsletter sera bientôt disponible.";
    },

    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
  },
};
</script>

<style scoped>
/* =============================================
   PRODU PNEU — PREMIUM FOOTER
============================================= */

.pp-footer {
  --pp-orange: #ff7900;
  --pp-orange-hover: #e76c00;
  --pp-dark: #151a20;
  --pp-footer-text: #aeb6be;
  --pp-footer-border: rgba(255, 255, 255, .10);

  width: 100%;
  font-family: "Poppins", sans-serif;
  color: #fff;
  background: var(--pp-dark);
}

.pp-footer *,
.pp-footer *::before,
.pp-footer *::after {
  box-sizing: border-box;
}

.pp-footer svg {
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* =============================================
   NEWSLETTER
============================================= */

.pp-newsletter {
  background: linear-gradient(
    110deg,
    #ff7900 0%,
    #f46f00 55%,
    #df5d00 100%
  );
  padding: 30px 0;
}

.pp-newsletter-inner {
  display: flex;
  align-items: center;
  gap: 19px;
}

.pp-newsletter-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 55px;
  height: 55px;
  border-radius: 12px;
  background: rgba(255, 255, 255, .16);
  color: #fff;
}

.pp-newsletter-icon svg {
  width: 29px;
  height: 29px;
}

.pp-newsletter-copy {
  flex: 1;
  min-width: 0;
}

.pp-newsletter-copy h3 {
  color: #fff;
  font-size: 19px;
  font-weight: 900;
  margin: 0 0 5px;
  line-height: 1.4;
}

.pp-newsletter-copy p {
  color: rgba(255, 255, 255, .91);
  font-size: 11px;
  line-height: 1.6;
  margin: 0;
}

.pp-newsletter-form {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 390px;
  height: 49px;
  padding: 4px;
  background: #fff;
  border-radius: 9px;
  margin: 0;
}

.pp-newsletter-form input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 14px;
  border: 0;
  outline: 0;
  background: transparent;
  font-family: inherit;
  font-size: 11px;
  color: #22282e;
}

.pp-newsletter-form input::placeholder {
  color: #9ba1a8;
}

.pp-newsletter-form button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  padding: 0 17px;
  background: #192027;
  border: 0;
  border-radius: 6px;
  color: #fff;
  font-family: inherit;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
  transition: background .2s;
}

.pp-newsletter-form button:hover {
  background: #343d45;
}

.pp-newsletter-form button svg {
  width: 16px;
  height: 16px;
}

.pp-newsletter-notice {
  font-size: 11px;
  font-weight: 600;
  margin: 12px 0 0;
  color: #fff;
}

/* =============================================
   MAIN FOOTER
============================================= */

.pp-footer-main {
  padding: 65px 0 0;
  background:
    radial-gradient(
      circle at 95% 0%,
      rgba(255, 121, 0, .045),
      transparent 36%
    ),
    #151a20;
}

.pp-footer-grid {
  display: grid;
  grid-template-columns:
    1.45fr 1fr 1fr 1.22fr;
  gap: 45px;
}

/* BRAND COLUMN */

.pp-footer-brand {
  min-width: 0;
}

.pp-footer-logo {
  display: inline-flex;
  align-items: center;
  text-decoration: none !important;
  margin-bottom: 18px;
}

.pp-footer-logo img {
  width: 205px;
  max-width: 100%;
  height: 73px;
  object-fit: contain;
  object-position: left center;
}

.pp-brand-description {
  color: var(--pp-footer-text);
  font-size: 12px;
  line-height: 1.95;
  margin: 0 0 20px;
  max-width: 320px;
}

.pp-brand-tagline {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #ebedf0;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 1.1px;
  margin-bottom: 23px;
}

.pp-brand-tagline span {
  display: block;
  width: 22px;
  height: 2px;
  background: var(--pp-orange);
}

/* SOCIALS */

.pp-socials {
  display: flex;
  align-items: center;
  gap: 9px;
}

.pp-socials a {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 37px;
  height: 37px;
  border: 1px solid var(--pp-footer-border);
  border-radius: 8px;
  color: #fff;
  background: rgba(255, 255, 255, .035);
  transition:
    background .25s,
    border-color .25s,
    transform .25s;
}

.pp-socials a:hover {
  background: var(--pp-orange);
  border-color: var(--pp-orange);
  transform: translateY(-3px);
}

.pp-socials svg {
  width: 17px;
  height: 17px;
}

/* FOOTER COLUMNS */

.pp-footer-column {
  min-width: 0;
}

.pp-footer-column h3 {
  margin: 4px 0 11px;
  color: #fff;
  font-size: 15px;
  font-weight: 850;
  line-height: 1.4;
}

.pp-title-line {
  display: block;
  width: 28px;
  height: 3px;
  background: var(--pp-orange);
  border-radius: 3px;
  margin-bottom: 23px;
}

.pp-footer-column ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.pp-footer-column ul li {
  margin-bottom: 12px;
}

.pp-footer-column ul a {
  display: inline-flex;
  align-items: center;
  color: var(--pp-footer-text);
  font-size: 11px;
  line-height: 1.6;
  text-decoration: none !important;
  transition: color .2s, transform .2s;
}

.pp-footer-column ul a:hover {
  color: var(--pp-orange);
  transform: translateX(3px);
}

.pp-company-links {
  margin-bottom: 17px !important;
}

/* CONTACT */

.pp-contact-items {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
}

.pp-contact-item {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  color: #c7cdd2;
  font-size: 11px;
  line-height: 1.7;
  text-decoration: none !important;
  overflow-wrap: anywhere;
}

.pp-contact-item:hover {
  color: var(--pp-orange);
}

.pp-contact-icon {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  color: var(--pp-orange);
  margin-top: 2px;
}

.pp-contact-icon svg {
  width: 17px;
  height: 17px;
}

.pp-contact-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: 1px solid var(--pp-orange);
  border-radius: 8px;
  padding: 12px 15px;
  color: var(--pp-orange);
  font-size: 11px;
  font-weight: 800;
  text-decoration: none !important;
  transition: background .2s, color .2s;
}

.pp-contact-button:hover {
  background: var(--pp-orange);
  color: #fff;
}

.pp-contact-button svg {
  width: 16px;
  height: 16px;
}

/* =============================================
   ASSURANCE ROW
============================================= */

.pp-footer-assurance {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;
  margin-top: 50px;
  padding: 25px 0;
  border-top: 1px solid var(--pp-footer-border);
}

.pp-assurance-item {
  display: flex;
  align-items: center;
  gap: 11px;
  color: #aeb5bc;
  font-size: 11px;
  font-weight: 600;
}

.pp-assurance-item svg {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  color: var(--pp-orange);
}

/* =============================================
   BOTTOM BAR
============================================= */

.pp-footer-bottom {
  background: #101419;
  border-top: 1px solid rgba(255, 255, 255, .06);
  padding: 20px 0;
}

.pp-footer-bottom-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.pp-footer-bottom p {
  color: #858e96;
  font-size: 10px;
  margin: 0;
}

.pp-footer-bottom p strong {
  color: #cdd2d7;
  font-weight: 700;
}

.pp-legal-links {
  display: flex;
  align-items: center;
  gap: 17px;
}

.pp-legal-links a {
  color: #9ca5ad;
  font-size: 10px;
  white-space: nowrap;
  text-decoration: none !important;
  transition: color .2s;
}

.pp-legal-links a:hover {
  color: var(--pp-orange);
}

.pp-back-top {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  margin-left: 10px;
  background: var(--pp-orange);
  color: #fff;
  border: 0;
  border-radius: 7px;
  cursor: pointer;
}

.pp-back-top:hover {
  background: var(--pp-orange-hover);
}

.pp-back-top svg {
  width: 17px;
  height: 17px;
}

/* =============================================
   RESPONSIVE
============================================= */

@media (max-width: 1199px) {
  .pp-footer-grid {
    grid-template-columns: 1.3fr 1fr 1fr 1.1fr;
    gap: 26px;
  }

  .pp-newsletter-form {
    width: 340px;
  }
}

@media (max-width: 991px) {
  .pp-newsletter-inner {
    flex-wrap: wrap;
  }

  .pp-newsletter-copy {
    flex: 1;
  }

  .pp-newsletter-form {
    width: 100%;
  }

  .pp-footer-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 42px 30px;
  }

  .pp-footer-assurance {
    gap: 18px;
  }
}

@media (max-width: 767px) {
  .pp-newsletter {
    padding: 27px 0;
  }

  .pp-newsletter-icon {
    width: 46px;
    height: 46px;
  }

  .pp-newsletter-copy h3 {
    font-size: 17px;
  }

  .pp-footer-main {
    padding-top: 45px;
  }

  .pp-footer-grid {
    gap: 35px 24px;
  }

  .pp-footer-assurance {
    grid-template-columns: 1fr;
    gap: 17px;
    margin-top: 35px;
  }

  .pp-footer-bottom-inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .pp-legal-links {
    width: 100%;
    flex-wrap: wrap;
  }

  .pp-back-top {
    margin-left: auto;
  }
}

@media (max-width: 575px) {
  .pp-newsletter-inner {
    align-items: flex-start;
    gap: 12px;
  }

  .pp-newsletter-icon {
    display: none;
  }

  .pp-newsletter-copy {
    flex-basis: 100%;
  }

  .pp-newsletter-form input {
    padding: 0 9px;
  }

  .pp-newsletter-form button {
    padding: 0 12px;
    font-size: 10px;
  }

  .pp-footer-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 35px 18px;
  }

  .pp-footer-brand,
  .pp-contact-column {
    grid-column: 1 / -1;
  }

  .pp-footer-logo img {
    width: 180px;
    height: 65px;
  }

  .pp-footer-column h3 {
    font-size: 13px;
  }

  .pp-footer-column ul a {
    font-size: 10px;
  }

  .pp-footer-bottom {
    padding: 22px 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pp-footer a,
  .pp-footer button {
    transition: none;
  }
}
</style>
